import axios, { type AxiosError } from "axios";

import {
  BROKEN_IN_APP_LINK_FINGERPRINT_KEY,
  BROKEN_IN_APP_LINK_MESSAGE_PREFIX,
  ENDPOINT_ALL_DIGITS_SEGMENT,
  ENDPOINT_CONTAINS_DIGIT,
  ENDPOINT_ID_PLACEHOLDER,
  ENDPOINT_OPAQUE_TOKEN_MIN_LENGTH,
  ENDPOINT_PLACEHOLDER_BASE,
  ENDPOINT_PREFIXED_HASHID_SEGMENT,
  ENDPOINT_UUID_SEGMENT,
  ENDPOINT_VERSION_SEGMENT,
  HTTP_CLIENT_ERROR_MIN_STATUS,
  HTTP_SERVER_ERROR_MIN_STATUS,
  INITIAL_LOCATION_KEY,
  NETWORK_ERROR_CODES,
  ROUTE_NOT_FOUND_MESSAGE_PREFIX,
  SENTRY_ENDPOINT_TAG,
  SENTRY_NOISE_CATEGORIES,
  SENTRY_NOISE_LEVEL,
  SENTRY_NOISE_TAG,
  SENTRY_ROUTE_PATH_TAG,
  UNKNOWN_ENDPOINT,
  UNKNOWN_METHOD,
} from "@/shared/configs/sentry.configs";
import type { AxiosNoiseClassification, RouteNotFoundReport } from "@/shared/types/sentryNoise.types";

const PLACEHOLDER_ORIGIN = new URL(ENDPOINT_PLACEHOLDER_BASE).origin;

const UNCHANGED: AxiosNoiseClassification = { kind: "unchanged" };

const isOpaqueIdentifier = (segment: string): boolean =>
  ENDPOINT_ALL_DIGITS_SEGMENT.test(segment)
  || ENDPOINT_UUID_SEGMENT.test(segment)
  || ENDPOINT_PREFIXED_HASHID_SEGMENT.test(segment)
  || (ENDPOINT_CONTAINS_DIGIT.test(segment) && segment.length >= ENDPOINT_OPAQUE_TOKEN_MIN_LENGTH);

const normalizeSegment = (segment: string): string => {
  if (ENDPOINT_VERSION_SEGMENT.test(segment)) return segment;
  return isOpaqueIdentifier(segment) ? ENDPOINT_ID_PLACEHOLDER : segment;
};

// Query and hash are dropped and ids replaced so the result is a stable grouping key
// that carries no personal data.
export const normalizeEndpoint = (raw: string | undefined): string => {
  if (!raw) return UNKNOWN_ENDPOINT;
  try {
    const url = new URL(raw, ENDPOINT_PLACEHOLDER_BASE);
    const path = url.pathname.split("/").map(normalizeSegment).join("/");
    return url.origin === PLACEHOLDER_ORIGIN ? path : `${url.host}${path}`;
  }
  catch {
    return UNKNOWN_ENDPOINT;
  }
};

const isNetworkFailure = (error: AxiosError): boolean =>
  !error.response && error.code !== undefined && NETWORK_ERROR_CODES.includes(error.code);

const isClientError = (status: number | undefined): status is number =>
  status !== undefined
  && status >= HTTP_CLIENT_ERROR_MIN_STATUS
  && status < HTTP_SERVER_ERROR_MIN_STATUS;

// The fingerprint is built from the normalized endpoint only: the raw url may carry
// ids or query params that are personal data.
const buildNoise = (
  category: string,
  method: string,
  endpoint: string,
  fingerprintSuffix: string[] = [],
): AxiosNoiseClassification => ({
  kind: "noise",
  level: SENTRY_NOISE_LEVEL,
  fingerprint: [category, method, endpoint, ...fingerprintSuffix],
  tags: { [SENTRY_NOISE_TAG]: category, [SENTRY_ENDPOINT_TAG]: endpoint },
});

export const classifyAxiosNoise = (error: unknown): AxiosNoiseClassification => {
  if (axios.isCancel(error)) return { kind: "drop" };
  if (!axios.isAxiosError(error)) return UNCHANGED;

  const method = error.config?.method?.toUpperCase() ?? UNKNOWN_METHOD;
  const endpoint = normalizeEndpoint(error.config?.url);

  if (isNetworkFailure(error)) {
    return buildNoise(SENTRY_NOISE_CATEGORIES.NETWORK, method, endpoint);
  }

  const status = error.response?.status;
  if (isClientError(status)) {
    return buildNoise(SENTRY_NOISE_CATEGORIES.CLIENT_ERROR, method, endpoint, [String(status)]);
  }

  return UNCHANGED;
};

// A first location means nobody clicked an in-app link (typed URL, bookmark, external link): that is
// expected noise. Any other key means our own UI generated the dead link, which must alert.
// Only the normalized path is reported: the raw pathname may carry ids.
export const classifyRouteNotFound = (locationKey: string, pathname: string): RouteNotFoundReport => {
  const path = normalizeEndpoint(pathname);

  if (locationKey === INITIAL_LOCATION_KEY) {
    return {
      message: `${ROUTE_NOT_FOUND_MESSAGE_PREFIX}${path}`,
      level: "info",
      fingerprint: [SENTRY_NOISE_CATEGORIES.ROUTE_NOT_FOUND],
      tags: {
        [SENTRY_NOISE_TAG]: SENTRY_NOISE_CATEGORIES.ROUTE_NOT_FOUND,
        [SENTRY_ROUTE_PATH_TAG]: path,
      },
    };
  }

  return {
    message: `${BROKEN_IN_APP_LINK_MESSAGE_PREFIX}${path}`,
    level: "error",
    fingerprint: [BROKEN_IN_APP_LINK_FINGERPRINT_KEY, path],
    tags: {},
  };
};
